'use client';
import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { Modal } from './Modal';
import { Spinner } from './states';

/** The full-size view of a picture: a chat attachment, one the agent shared, generated or read, or a
 *  screenshot in a guide. It is the app's own `Modal` with the frameless chrome, so what the reader gets
 *  is a scrim and the picture: no header, no title drawn over the image, no edge, no ground, no cast shadow,
 *  no close button. A dialog's frame holds a title row, controls and a body of text, and an enlarged picture
 *  is the one surface in the app that has no use for any of them.
 *
 *  Everything the frame used to CARRY is still there, and still comes from the shared seam, which is exactly
 *  why this is a `Modal` rather than a second overlay beside it: the portal, the overlay stack and its
 *  `inert` isolation, the body scroll lock, the focus trap, Escape, the one backdrop rule that decides a
 *  dismissal, and the return of focus to the control the picture was opened from. A bare surface is not a
 *  thing a pointer hits (see `chrome` on `DialogContent`), so a press anywhere that is not the picture falls
 *  through to the backdrop that closes the view, and a press on the picture is a press on the picture.
 *
 *  Geometry belongs to the backdrop: it pads itself to the safe area on every edge, and one
 *  `object-contain` picture inside that box is what keeps a panorama and a tall screenshot whole on a phone
 *  in either orientation and on a desktop. While the full-size bytes are in flight the wait is said quietly
 *  on the surface, and bytes that cannot be had at all are stated in place of the picture rather than left
 *  as a broken glyph. `src` is the browser URL of the picture (same-origin, so the session proxy applies). */
export function ImageLightbox({ src, description, onClose }: {
  src: string;
  description: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <Modal
      title={t.common.imageViewerTitle}
      onClose={onClose}
      chrome="bare"
      presentation="center"
    >
      {failed ? (
        <span data-testid="image-lightbox-gone" className="pointer-events-auto inline-flex items-center gap-2 text-meta text-muted-foreground">
          <ImageOff size={16} className="shrink-0" aria-hidden />
          {t.common.imageGone}
        </span>
      ) : (
        <img
          src={src}
          alt={description}
          data-testid="image-lightbox"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          // The picture opts back in: it is the one thing on this surface a click belongs to, and a click
          // that lands on it must not reach the rule that closes the view.
          className="pointer-events-auto block max-h-full max-w-full bg-image-ground object-contain"
        />
      )}
      {loaded || failed ? null : (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Spinner size="lg" label={t.common.loading} />
        </div>
      )}
    </Modal>
  );
}
